/**
 * Step: Configure Product Quantities
 * Product-specific step with per-lot unit config selection, pallet/case/unit inputs,
 * required lot/batch/dates with auto-calculated shelf life.
 * Single item: per-variant lot sections. Multi item: accordion per product with multi-variant lots.
 *
 * Data structure:
 *   Each lot stores its own config: lot._configId, lot._configName, lot._unitsPerCase, ...
 *   formData.variantLots = { [variantId]: [lot1, lot2, ...] }  (single-item with variants)
 *   formData.lots = [lot1, lot2, ...]                          (single-item without variants)
 *   itemsConfig[productId].variantLots = { [variantId]: [lot1, ...] } (multi-item with variants)
 *   itemsConfig[productId].lots = [lot1, ...]                        (multi-item without variants)
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, useTheme, alpha, Button, Paper, Chip
} from '@mui/material';
import {
  Add as AddIcon
} from '@mui/icons-material';
import { supabase } from '../../../../../supabaseClient';
import ConfigSelectionDialog from './ConfigSelectionDialog';
import VariantSelectionDialog from './VariantSelectionDialog';
import ProductAccordionCard from './ProductAccordionCard';
import ProductLotEntry from './ProductLotEntry';

const EMPTY_LOT = {
  palletQty: '', caseQty: '', unitQty: '',
  lotNumber: '', batchNumber: '',
  mfgDate: '', expiryDate: ''
};

function extractSize(name) {
  const match = name?.match(/(\d+(\.\d+)?)/);
  return match ? parseFloat(match[1]) : 0;
}

// Calculate total base units using per-lot config
function calcTotal(lots) {
  return (lots || []).reduce((sum, lot) => {
    const upc = parseInt(lot._unitsPerCase) || 1;
    const cpp = parseInt(lot._casesPerPallet) || 1;
    const p = parseInt(lot.palletQty) || 0;
    const c = parseInt(lot.caseQty) || 0;
    const u = parseInt(lot.unitQty) || 0;
    return sum + (p * cpp * upc) + (c * upc) + u;
  }, 0);
}

export default function StepProductQuantity({
  formData,
  onFormChange,
  suppliers,
  config,
  selectedItems,
  selectedVariants = [],
  itemsConfig,
  onItemsConfigChange,
  companyId
}) {
  const theme = useTheme();

  // Config dialog state
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configSearch, setConfigSearch] = useState('');
  const [availableConfigs, setAvailableConfigs] = useState([]);
  // configTarget: { itemId?, variantId?, lotIndex }
  const [configTarget, setConfigTarget] = useState(null);

  // Variant dialog state
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [variantSearch, setVariantSearch] = useState('');
  const [variantForItemId, setVariantForItemId] = useState(null);
  const [variantList, setVariantList] = useState([]);

  // Multi-item: variants per product
  const [variantsMap, setVariantsMap] = useState({});

  const handleChange = (field, value) => {
    onFormChange({ ...formData, [field]: value });
  };

  // Multi-item: update entire product config
  const handleUpdateProductConfig = (itemId, newConfig) => {
    onItemsConfigChange({ ...itemsConfig, [itemId]: newConfig });
  };

  // Multi-item: fetch variants for all selected products
  useEffect(() => {
    if (selectedItems.length <= 1 || !companyId) return;
    (async () => {
      const productIds = selectedItems.map(i => i.id);
      const { data } = await supabase
        .from('product_variants')
        .select('*')
        .in('product_id', productIds)
        .eq('is_active', true);
      const map = {};
      productIds.forEach(pid => { map[pid] = []; });
      (data || []).forEach(v => {
        if (map[v.product_id]) map[v.product_id].push(v);
      });
      setVariantsMap(map);
    })();
  }, [selectedItems, companyId]);

  // Initialize lots array for multi-item products that don't have one yet
  useEffect(() => {
    if (selectedItems.length <= 1) return;
    let updated = false;
    const newConfig = { ...itemsConfig };
    selectedItems.forEach(item => {
      if (!newConfig[item.id]?.lots && !newConfig[item.id]?.variantLots) {
        newConfig[item.id] = {
          ...(newConfig[item.id] || {}),
          lots: [{ ...EMPTY_LOT }]
        };
        updated = true;
      }
    });
    if (updated) onItemsConfigChange(newConfig);
  }, [selectedItems]);

  // Open variant dialog (multi-item)
  const openVariantDialog = (itemId) => {
    const variants = variantsMap[itemId] || [];
    setVariantForItemId(itemId);
    setVariantList(variants);
    setVariantSearch('');
    setVariantDialogOpen(true);
  };

  const handleVariantSelect = (selectedIds, variant) => {
    const existing = itemsConfig[variantForItemId] || {};
    const variants = variantsMap[variantForItemId] || [];
    const selectedVariantObjects = variants.filter(v => selectedIds.includes(v.id));

    const oldVariantLots = existing.variantLots || {};
    const newVariantLots = {};
    selectedIds.forEach(vid => {
      newVariantLots[vid] = oldVariantLots[vid] || [{ ...EMPTY_LOT }];
    });

    handleUpdateProductConfig(variantForItemId, {
      ...existing,
      variantIds: selectedIds,
      variantNames: selectedVariantObjects.map(v => v.name),
      variantLots: newVariantLots,
      variantId: selectedIds.length === 1 ? selectedIds[0] : null,
      variantName: selectedIds.length === 1 ? selectedVariantObjects[0]?.name : null
    });
  };

  // Open config dialog for a specific lot
  // context = { itemId?, variantId?, lotIndex }
  const openConfigDialog = async (context) => {
    setConfigTarget(context);
    setConfigDialogOpen(true);
    setConfigSearch('');
    const { data } = await supabase
      .from('unit_configurations')
      .select('*')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('name');
    setAvailableConfigs(data || []);
  };

  const handleConfigSelect = (cfg) => {
    const lotConfig = {
      _configId: cfg.id,
      _configName: cfg.name,
      _unitsPerCase: cfg.units_per_case || 1,
      _casesPerPallet: cfg.cases_per_pallet || 1,
      _enableCases: cfg.enable_cases ?? true,
      _enablePallets: cfg.enable_pallets ?? false
    };

    const { itemId, variantId, lotIndex } = configTarget || {};

    if (itemId) {
      // Multi-item
      const existing = itemsConfig[itemId] || {};
      if (variantId) {
        const vLots = [...((existing.variantLots || {})[variantId] || [])];
        vLots[lotIndex] = { ...(vLots[lotIndex] || { ...EMPTY_LOT }), ...lotConfig };
        handleUpdateProductConfig(itemId, {
          ...existing,
          variantLots: { ...(existing.variantLots || {}), [variantId]: vLots }
        });
      } else {
        const lots = [...(existing.lots || [{ ...EMPTY_LOT }])];
        lots[lotIndex] = { ...(lots[lotIndex] || { ...EMPTY_LOT }), ...lotConfig };
        handleUpdateProductConfig(itemId, { ...existing, lots });
      }
    } else if (variantId) {
      // Single-item, variant lot
      const vLots = [...((formData.variantLots || {})[variantId] || [])];
      vLots[lotIndex] = { ...(vLots[lotIndex] || { ...EMPTY_LOT }), ...lotConfig };
      onFormChange({ ...formData, variantLots: { ...(formData.variantLots || {}), [variantId]: vLots } });
    } else {
      // Single-item, flat lot
      const lots = [...(formData.lots || [{ ...EMPTY_LOT }])];
      lots[lotIndex] = { ...(lots[lotIndex] || { ...EMPTY_LOT }), ...lotConfig };
      onFormChange({ ...formData, lots });
    }

    setConfigDialogOpen(false);
    setConfigTarget(null);
  };

  const getCurrentConfigId = () => {
    if (!configTarget) return null;
    const { itemId, variantId, lotIndex } = configTarget;
    if (itemId) {
      const cfg = itemsConfig[itemId] || {};
      if (variantId) {
        const vLots = (cfg.variantLots || {})[variantId] || [];
        return vLots[lotIndex]?._configId || null;
      }
      return (cfg.lots || [])[lotIndex]?._configId || null;
    }
    if (variantId) {
      const vLots = (formData.variantLots || {})[variantId] || [];
      return vLots[lotIndex]?._configId || null;
    }
    return (formData.lots || [])[lotIndex]?._configId || null;
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(10px)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(theme.palette.primary.main, 0.3),
        borderWidth: 1
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 1
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 1
      }
    },
    '& .MuiInputLabel-root': { fontWeight: 500 }
  };

  const isMultiItem = selectedItems.length > 1;

  // ── Single-item lots management ──
  const hasVariants = selectedVariants.length > 0;
  const variantLots = formData.variantLots || {};
  const singleLots = formData.lots?.length > 0 ? formData.lots : [{ ...EMPTY_LOT }];

  // Initialize variantLots when variants are selected
  useEffect(() => {
    if (!hasVariants || isMultiItem) return;
    let updated = false;
    const newVL = { ...(formData.variantLots || {}) };
    selectedVariants.forEach(v => {
      if (!newVL[v.id]) {
        newVL[v.id] = [{ ...EMPTY_LOT }];
        updated = true;
      }
    });
    Object.keys(newVL).forEach(vid => {
      if (!selectedVariants.some(v => v.id === vid)) {
        delete newVL[vid];
        updated = true;
      }
    });
    if (updated) onFormChange({ ...formData, variantLots: newVL });
  }, [selectedVariants]);

  // Flat lot handlers (no variants)
  const handleSingleLotChange = (lotIndex, updatedLot) => {
    const newLots = [...singleLots];
    newLots[lotIndex] = updatedLot;
    onFormChange({ ...formData, lots: newLots });
  };
  const handleAddSingleLot = () => {
    onFormChange({ ...formData, lots: [...singleLots, { ...EMPTY_LOT }] });
  };
  const handleRemoveSingleLot = (lotIndex) => {
    const newLots = singleLots.filter((_, i) => i !== lotIndex);
    onFormChange({ ...formData, lots: newLots.length > 0 ? newLots : [{ ...EMPTY_LOT }] });
  };

  // Per-variant lot handlers
  const handleVariantLotChange = (variantId, lotIndex, updatedLot) => {
    const vLots = [...(variantLots[variantId] || [{ ...EMPTY_LOT }])];
    vLots[lotIndex] = updatedLot;
    onFormChange({ ...formData, variantLots: { ...variantLots, [variantId]: vLots } });
  };
  const handleAddVariantLot = (variantId) => {
    const vLots = variantLots[variantId] || [{ ...EMPTY_LOT }];
    onFormChange({ ...formData, variantLots: { ...variantLots, [variantId]: [...vLots, { ...EMPTY_LOT }] } });
  };
  const handleRemoveVariantLot = (variantId, lotIndex) => {
    const vLots = (variantLots[variantId] || []).filter((_, i) => i !== lotIndex);
    onFormChange({ ...formData, variantLots: { ...variantLots, [variantId]: vLots.length > 0 ? vLots : [{ ...EMPTY_LOT }] } });
  };

  const singleTotalBase = hasVariants
    ? Object.values(variantLots).reduce((sum, lots) => sum + calcTotal(lots), 0)
    : calcTotal(singleLots);

  const sortedVariants = [...selectedVariants].sort((a, b) => extractSize(b.name) - extractSize(a.name));

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', fontSize: '1.05rem' }}>
        Configure the product quantity and lot details
      </Typography>

      {/* ════════════════════════════ SINGLE ITEM MODE ════════════════════════════ */}
      {!isMultiItem && (
        <>
          {/* ── Per-Variant Lot Sections ── */}
          {hasVariants ? (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Lot Details by Variant ({sortedVariants.length} variant{sortedVariants.length > 1 ? 's' : ''})
                </Typography>
                {singleTotalBase > 0 && (
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                    Grand Total: {singleTotalBase.toLocaleString()} base units
                  </Typography>
                )}
              </Box>

              {sortedVariants.map((variant) => {
                const vLots = variantLots[variant.id] || [{ ...EMPTY_LOT }];
                const variantTotal = calcTotal(vLots);
                return (
                  <Paper
                    key={variant.id}
                    elevation={0}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                      overflow: 'hidden'
                    }}
                  >
                    {/* Variant header */}
                    <Box sx={{
                      px: 2.5, py: 1.5,
                      bgcolor: alpha(theme.palette.grey[100], 0.6),
                      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                          {variant.name}
                        </Typography>
                        {variant.sku && (
                          <Chip label={variant.sku} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 22 }} />
                        )}
                      </Box>
                      {variantTotal > 0 && (
                        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                          {variantTotal.toLocaleString()} units
                          {vLots.length > 1 && ` · ${vLots.length} lots`}
                        </Typography>
                      )}
                    </Box>

                    {/* Lot entries for this variant */}
                    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {vLots.map((lot, idx) => (
                        <ProductLotEntry
                          key={idx}
                          lot={lot}
                          lotIndex={idx}
                          totalLots={vLots.length}
                          onOpenConfigDialog={(lotIdx) => openConfigDialog({ variantId: variant.id, lotIndex: lotIdx })}
                          onChange={(lotIdx, updatedLot) => handleVariantLotChange(variant.id, lotIdx, updatedLot)}
                          onRemove={(lotIdx) => handleRemoveVariantLot(variant.id, lotIdx)}
                          textFieldSx={textFieldSx}
                        />
                      ))}
                    </Box>

                    {/* Add lot button */}
                    <Box sx={{ px: 2.5, pb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddVariantLot(variant.id)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2.5,
                          py: 0.8,
                          borderStyle: 'dashed',
                          borderColor: alpha(theme.palette.grey[400], 0.8),
                          color: 'text.secondary',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            borderStyle: 'dashed',
                            bgcolor: alpha(theme.palette.primary.main, 0.04)
                          }
                        }}
                      >
                        Add Another Lot
                      </Button>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            /* ── No Variants: flat lot list ── */
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Lot Details
                </Typography>
                {singleTotalBase > 0 && (
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                    Total: {singleTotalBase.toLocaleString()} base units
                    {singleLots.length > 1 && ` across ${singleLots.length} lots`}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {singleLots.map((lot, idx) => (
                  <ProductLotEntry
                    key={idx}
                    lot={lot}
                    lotIndex={idx}
                    totalLots={singleLots.length}
                    onOpenConfigDialog={(lotIdx) => openConfigDialog({ variantId: null, lotIndex: lotIdx })}
                    onChange={handleSingleLotChange}
                    onRemove={handleRemoveSingleLot}
                    textFieldSx={textFieldSx}
                  />
                ))}
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddSingleLot}
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  px: 2.5,
                  py: 0.8,
                  borderStyle: 'dashed',
                  borderColor: alpha(theme.palette.grey[400], 0.8),
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    borderStyle: 'dashed',
                    bgcolor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              >
                Add Another Lot
              </Button>
            </Box>
          )}

          {/* Notes */}
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes (Optional)"
              value={formData.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              sx={textFieldSx}
            />
          </Box>
        </>
      )}

      {/* ════════════════════════════ MULTI-ITEM MODE ════════════════════════════ */}
      {isMultiItem && (
        <Box>
          {selectedItems.map((item, idx) => (
            <ProductAccordionCard
              key={item.id}
              item={item}
              itemConfig={itemsConfig[item.id]}
              variants={variantsMap[item.id] || []}
              onOpenVariantDialog={openVariantDialog}
              onOpenConfigForLot={(variantId, lotIndex) => openConfigDialog({ itemId: item.id, variantId, lotIndex })}
              onUpdateConfig={handleUpdateProductConfig}
              textFieldSx={textFieldSx}
              defaultExpanded={idx === 0}
            />
          ))}

          {/* Shared notes */}
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes (Optional — applies to all)"
              value={formData.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              sx={textFieldSx}
            />
          </Box>
        </Box>
      )}

      {/* ════════════════════════════ DIALOGS ════════════════════════════ */}
      <ConfigSelectionDialog
        open={configDialogOpen}
        onClose={() => { setConfigDialogOpen(false); setConfigTarget(null); }}
        configs={availableConfigs}
        search={configSearch}
        onSearchChange={setConfigSearch}
        onSelect={handleConfigSelect}
        currentConfigId={getCurrentConfigId()}
      />

      <VariantSelectionDialog
        open={variantDialogOpen}
        onClose={() => { setVariantDialogOpen(false); setVariantForItemId(null); }}
        variants={variantList}
        search={variantSearch}
        onSearchChange={setVariantSearch}
        onSelect={handleVariantSelect}
        multiSelect
        selectedVariantIds={variantForItemId ? (itemsConfig[variantForItemId]?.variantIds || []) : []}
        currentVariantId={null}
      />
    </Box>
  );
}
